import { Flex } from '@chakra-ui/react';
import { Modal, Button, useToast, Checkbox } from '@morressier/alembic';
import { useEditorialUploadICMJEForm } from 'src/api/commands';
import { IDecisionModalPropsForSubmittingAuthor } from '../decisionModals/decisionModalHelper.js';
import { FormProvider, useForm } from 'react-hook-form';
import { useWorkflow } from 'src/hooks/useWorkflow';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { CustomFormFieldDefinition } from '@publishing-engine/domain/types/article';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { GenericFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/generic-form-fields/generic-form-fields';
import { generateValidationSchema } from 'src/utils/validation';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';

export const EditorialUploadICMJEFormForAllModal = ({
  onClose,
  submissionId,
  submissionContextId,
  authorEmail,
  authorStatus = false,
  icmjeFormList
}: IDecisionModalPropsForSubmittingAuthor) => {
  const {
    forms: { editorialUploadICMJE },
    articleWorkflow
  } = useWorkflow();

  if (!editorialUploadICMJE) {
    return <></>;
  }

  const toast = useToast();

  const { refetchSubmission, organizationId } = useSubmissionData();
  const editorialUploadICMJEMutation = useEditorialUploadICMJEForm({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: articleWorkflow.id,
    invalidateSubmissionQueries: refetchSubmission
  });

  const uploadICMJEFields = new GenericFormFields(editorialUploadICMJE);

  const config = {
    ICMJEFormFile: uploadICMJEFields.getFieldConfig(
      'ICMJEFormFile'
    ) as CustomFormFieldDefinition
  };
  const schema = generateValidationSchema(config);

  const methods = useForm({
    defaultValues: {
      icmjeForm: {
        file: {},
        isBulkUpload: false
      }
    },
    resolver: ajvResolver(schema)
  });
  const { handleSubmit, getValues, register } = methods;

  const uploadICMJEForm = async (payload: unknown) => {
    const res = await editorialUploadICMJEMutation.mutateAsync(payload);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Forms and Disclosures Uploaded'
      });
    }
  };

  const submit = async () => {
    const formValues = getValues();

    if (formValues.icmjeForm.isBulkUpload === true) {
      if (!icmjeFormList?.length) return;

      for (const icmje of icmjeFormList) {
        const payload = {
          ...formValues,
          icmjeForm: {
            ...formValues.icmjeForm,
            email: icmje.email,
            isSubmittingAuthor: icmje.isSubmittingAuthor,
            isBulkUpload: true
          }
        };

        try {
          await uploadICMJEForm(payload);
        } catch {
          toast({
            variant: 'error',
            title: `Error uploading Forms and Disclosures for ${icmje.email}`
          });
        }
      }
    } else {
      const payload = {
        ...formValues,
        icmjeForm: {
          ...formValues.icmjeForm,
          email: authorEmail,
          isSubmittingAuthor: authorStatus
        }
      };
      await uploadICMJEForm(payload);
    }
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} width="720px">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          <Flex padding={8} gap={6} flexDirection="column" overflow="auto">
            {config.ICMJEFormFile && (
              <CustomFormFieldComponent config={config.ICMJEFormFile} />
            )}
            <Checkbox
              id="uploadForAll"
              ariaLabel="UploadForAll"
              {...register('icmjeForm.isBulkUpload')}
            >
              Apply same for all the other authors
            </Checkbox>
            <Flex gap={2} justifyContent="flex-end">
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={editorialUploadICMJEMutation.isPending}
              >
                Upload
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
